import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Typography,
  alpha
} from '@mui/material';
import {
  KeyboardArrowDown,
  Dashboard,
  ManageAccounts,
  Logout
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import colors from '../../theme/colors';

const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, userData, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  if (!user) return null;

  const displayName = userData?.displayName || user.email?.split('@')[0] || 'User';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <>
      {/* Trigger — avatar-only circle button */}
      <Box
        onClick={handleMenuOpen}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          borderRadius: '50%',
          p: '2px',
          background: open
            ? `linear-gradient(135deg, ${colors.blue.electric}, #00BCD4)`
            : `linear-gradient(135deg, ${alpha(colors.blue.electric, 0.35)}, ${alpha('#00BCD4', 0.35)})`,
          transition: 'all 0.2s ease',
          boxShadow: open
            ? `0 0 0 3px ${alpha(colors.blue.electric, 0.2)}, 0 0 12px ${alpha(colors.blue.electric, 0.35)}`
            : 'none',
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.blue.electric}, #00BCD4)`,
            boxShadow: `0 0 0 3px ${alpha(colors.blue.electric, 0.15)}, 0 0 10px ${alpha(colors.blue.electric, 0.25)}`,
          },
        }}
      >
        <Avatar
          src={userData?.profilePicUrl}
          sx={{
            width: 36,
            height: 36,
            bgcolor: colors.background.elevated,
            fontSize: '0.85rem',
            fontWeight: 700,
            border: `1.5px solid ${colors.background.default}`,
          }}
        >
          {initial}
        </Avatar>
        {/* Chevron badge */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -1,
            right: -1,
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: colors.background.paper,
            border: `1px solid ${colors.border.subtle}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <KeyboardArrowDown
            sx={{
              fontSize: '0.65rem',
              color: alpha(colors.text.primary, 0.55),
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
      </Box>

      {/* Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            background: colors.background.paper,
            border: `1px solid ${colors.border.default}`,
            borderRadius: 2.5,
            boxShadow: `${colors.shadows.blue.md}, 0 4px 24px rgba(0,0,0,0.5)`,
            overflow: 'hidden',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User identity header */}
        <Box
          sx={{
            px: 2,
            py: 1.75,
            background: `linear-gradient(135deg, ${alpha(colors.blue.electric, 0.08)}, transparent)`,
            borderBottom: `1px solid ${colors.border.subtle}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: '2px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.blue.electric}, #00BCD4)`,
                flexShrink: 0,
              }}
            >
              <Avatar
                src={userData?.profilePicUrl}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: colors.background.elevated,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  border: `1.5px solid ${colors.background.paper}`,
                }}
              >
                {initial}
              </Avatar>
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography
                onClick={() => handleNavigation('/profile')}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: colors.text.primary,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  display: 'inline-block',
                  maxWidth: '100%',
                  transition: 'color 0.15s ease',
                  '&:hover': {
                    color: colors.blue.electric,
                    textDecoration: 'underline',
                  },
                }}
              >
                {displayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: alpha(colors.text.primary, 0.4),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: 0.25,
                }}
              >
                {user.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Nav items */}
        <Box sx={{ py: 0.75 }}>
          <MenuItem
            onClick={() => handleNavigation('/dashboard')}
            sx={{
              px: 2,
              py: 1.125,
              gap: 1.5,
              borderRadius: 1.5,
              mx: 0.75,
              transition: 'all 0.15s ease',
              '&:hover': {
                background: alpha(colors.blue.electric, 0.1),
                '& .menu-icon': { color: colors.blue.electric },
                '& .menu-label': { color: colors.text.primary },
              },
            }}
          >
            <Dashboard
              className="menu-icon"
              sx={{
                fontSize: '1.1rem',
                color: alpha(colors.text.primary, 0.45),
                transition: 'color 0.15s ease',
              }}
            />
            <Typography
              className="menu-label"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: alpha(colors.text.primary, 0.8),
                transition: 'color 0.15s ease',
              }}
            >
              My Leagues
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => handleNavigation('/profile')}
            sx={{
              px: 2,
              py: 1.125,
              gap: 1.5,
              borderRadius: 1.5,
              mx: 0.75,
              transition: 'all 0.15s ease',
              '&:hover': {
                background: alpha(colors.blue.electric, 0.1),
                '& .menu-icon': { color: colors.blue.electric },
                '& .menu-label': { color: colors.text.primary },
              },
            }}
          >
            <ManageAccounts
              className="menu-icon"
              sx={{
                fontSize: '1.1rem',
                color: alpha(colors.text.primary, 0.45),
                transition: 'color 0.15s ease',
              }}
            />
            <Typography
              className="menu-label"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: alpha(colors.text.primary, 0.8),
                transition: 'color 0.15s ease',
              }}
            >
              Edit Profile
            </Typography>
          </MenuItem>
        </Box>

        {/* Divider */}
        <Box sx={{ mx: 2, borderTop: `1px solid ${colors.border.subtle}` }} />

        {/* Logout */}
        <Box sx={{ py: 0.75 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 2,
              py: 1.125,
              gap: 1.5,
              borderRadius: 1.5,
              mx: 0.75,
              transition: 'all 0.15s ease',
              '&:hover': {
                background: alpha(colors.error.primary, 0.08),
                '& .logout-icon': { color: colors.error.light },
                '& .logout-label': { color: colors.error.light },
              },
            }}
          >
            <Logout
              className="logout-icon"
              sx={{
                fontSize: '1.1rem',
                color: alpha(colors.text.primary, 0.35),
                transition: 'color 0.15s ease',
              }}
            />
            <Typography
              className="logout-label"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: alpha(colors.text.primary, 0.55),
                transition: 'color 0.15s ease',
              }}
            >
              Sign out
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
};

export default UserMenu;
